/* eslint-disable sonarjs/no-duplicate-string */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import differenceInDays from 'date-fns/differenceInDays';
import { addUserProperties } from '@blocs.heap';
import { gqlBillingPlan, gqlCountry, gqlSubscription } from '@minecraft.graphql-types';
import { useQuery } from '@apollo/client';
import {
  ArtistSubscriptionFragment,
  BillingPlansDoc,
  BillingPlansQuery,
  DiscountTypeEnum,
  PlanTermEnum,
  TrialType,
} from '@minecraft.graphql-operations';
import { useUserContext } from '@blocs.auth';
import { TranslateFn, useTranslation } from '@blocs.i18n';
import moment, { MomentInput } from 'moment';
import { appConfig } from '@minecraft.app-config';
import { captureExceptionEvent } from '@blocs.observability';
import {
  PlanFeatureType,
  getPlanFeatures,
  ESSENTIAL_FEATURES,
  ESSENTIAL_WITH_FREE_FEATURES,
  FREE_FEATURES,
  FULL_ESSENTIAL_FEATURES,
  FULL_FREE_FEATURES,
  FULL_PREMIUM_FEATURES,
  PREMIUM_FEATURES,
  FULL_ULTIMATE_FEATURES,
  ULTIMATE_FEATURES,
} from './features';
import { PaywallLocation, paywallLocationToBillingPath } from './enums/PaywallLocation';
import { priceFormatter } from './helpers/priceFormatter';
import { getTrialTimeLeft } from './helpers/trialDuration';
import { PlanCode, TemperatureValues, BillingCodes, PlanProvider } from './enums/plans';
import { useOpenCallRedirect } from './useOpenCallRedirect';
import { useRegistrationLocation } from './useRegistrationLocation';
import { UpgradeModalExperimentGroups } from './enums/experimentGroups';

const NON_REACTIVATE_LOCATIONS = [
  PaywallLocation.SIGNUP,
  PaywallLocation.MEMBERSHIP,
  PaywallLocation.OVERAGE_UPSELL_MODAL,
  PaywallLocation.OVERAGE_MODAL,
  PaywallLocation.THRIVE_UPSELL,
];

export interface UsePlansProps {
  subscription?: ArtistSubscriptionFragment | gqlSubscription;
  paywallLocation?: PaywallLocation;
  artistId?: string | number;
  isModal?: boolean;
  modalVariant: UpgradeModalExperimentGroups;
}

export type PlanType = {
  name: string;
  code: PlanCode;
  title: string;
  description?: string;
  billingCode: gqlBillingPlan['billingCode'];
  icon?: string;
  heroImage: string;
  including?: string;
  billed?: string;
  month?: string;
  temperature: TemperatureValues;
  keyFeatures: PlanFeatureType[];
  features: PlanFeatureType[];
  combinedFeatures?: PlanFeatureType[];
  featuresTitle?: string;
  price: string | number;
  discountedPrice?: string | number;
  strikeThroughPrice?: string | number;
  cta?: string;
  order?: number;
  canUpgrade?: boolean;
  isCurrent?: boolean;
  level: number;
  term: {
    code: string;
    name?: string;
  };
  currencyCode?: string;
  canRenew?: boolean;
  iconName?: string;
  isSecondary?: boolean;
  titleWithBillingInterval?: string;
  trialLengthDays?: number;
  isTrialPeriodEnabled?: boolean;
  isUpgradeDisabled?: boolean;
  shouldHidePlan?: boolean;
  isUpgradePath?: boolean;
  customCTA?: string;
  handleCustomCTA?: () => void;
  isReactivation?: boolean;
  gradientColor?: string;
  isEssentialTrialPlan?: boolean;
  displayAddPaymentSection?: boolean;
  priceWithDecimal?: boolean;
  acceptTermsCheckboxKey?: string;
  discountPercent?: number;
  discountType?: DiscountTypeEnum;
  discountOfferText?: string;
  translationCode: BillingCodes;
  totalBillingCycles?: number;
  backgroundImage?: string;
  isCardlessTrial?: boolean;
  provider?: PlanProvider;
};

export interface PlanTemperatureProps {
  temperature?: TemperatureValues;
  isTrialPeriodEnabled?: boolean;
  isUpgradeDisabled?: boolean;
}

export const PLANS: PlanType[] = [
  {
    code: PlanCode.Ultimate,
    name: 'ultimate',
    gradientColor: 'blue',
    billingCode: BillingCodes.ultimateAnnual,
    translationCode: BillingCodes.ultimateAnnual,
    title: 'common:paywall.plan.ultimate_annual.title',
    description: 'common:paywall.plan.ultimate_annual.description',
    icon: 'ultimate',
    iconName: 'ultimate-check',
    heroImage: 'ultimate-hero',
    including: 'common:paywall.plan.ultimate_annual.including',
    billed: 'common:paywall.plan.billed_yearly',
    month: 'common:paywall.plan.month',
    temperature: TemperatureValues.spicy,
    keyFeatures: ULTIMATE_FEATURES,
    features: FULL_ULTIMATE_FEATURES,
    featuresTitle: 'common:paywall.plan.features.ultimate_annual.title',
    price: 0,
    discountedPrice: 0,
    cta: '',
    order: 3,
    level: 7,
    term: {
      code: PlanTermEnum.Annual,
    },
  },
  {
    code: PlanCode.Ultimate,
    name: 'ultimate',
    gradientColor: 'blue',
    billingCode: BillingCodes.ultimateMonth,
    translationCode: BillingCodes.ultimateMonth,
    title: 'common:paywall.plan.ultimate_m2m.title',
    description: 'common:paywall.plan.ultimate_m2m.description',
    icon: 'ultimate',
    iconName: 'ultimate-check',
    heroImage: 'ultimate-hero',
    including: 'common:paywall.plan.ultimate_m2m.including',
    billed: 'common:paywall.plan.billed_monthly',
    month: 'common:paywall.plan.month',
    temperature: TemperatureValues.spicy,
    keyFeatures: ULTIMATE_FEATURES,
    features: FULL_ULTIMATE_FEATURES,
    featuresTitle: 'common:paywall.plan.features.ultimate_m2m.title',
    price: 0,
    discountedPrice: 0,
    cta: '',
    order: 3,
    level: 6,
    term: {
      code: PlanTermEnum.Monthly,
    },
  },
  {
    code: PlanCode.Premium,
    name: 'Premium',
    billingCode: BillingCodes.premiumAnnual,
    translationCode: BillingCodes.premiumAnnual,
    title: 'common:paywall.plan.premium_annual.title',
    icon: 'diamondoutline',
    iconName: 'check-circle',
    heroImage: 'premium-plan',
    including: 'common:paywall.plan.premium_annual.including',
    billed: 'common:paywall.plan.billed_yearly',
    month: 'common:paywall.plan.month',
    temperature: TemperatureValues.cool,
    keyFeatures: PREMIUM_FEATURES,
    features: FULL_PREMIUM_FEATURES,
    featuresTitle: 'common:paywall.plan.features.premium_annual.title',
    price: 0,
    discountedPrice: 0,
    cta: '',
    order: 2,
    level: 5,
    term: {
      code: PlanTermEnum.Annual,
    },
  },
  {
    code: PlanCode.Premium,
    name: 'Premium',
    billingCode: BillingCodes.premiumMonth,
    translationCode: BillingCodes.premiumMonth,
    title: 'common:paywall.plan.premium_m2m.title',
    icon: 'diamondoutline',
    iconName: 'check-circle',
    heroImage: 'premium-plan',
    including: 'common:paywall.plan.premium_m2m.including',
    billed: 'common:paywall.plan.billed_monthly',
    month: 'common:paywall.plan.month',
    temperature: TemperatureValues.cool,
    keyFeatures: PREMIUM_FEATURES,
    features: FULL_PREMIUM_FEATURES,
    featuresTitle: 'common:paywall.plan.features.premium_m2m.title',
    price: 0,
    discountedPrice: 0,
    cta: '',
    order: 2,
    level: 4,
    term: {
      code: PlanTermEnum.Monthly,
    },
  },
  {
    code: PlanCode.Essential,
    name: 'Essential',
    billingCode: BillingCodes.essentialAnnual,
    translationCode: BillingCodes.essentialAnnual,
    title: 'common:paywall.plan.essential_annual.title',
    description: 'common:paywall.plan.essential_annual.description',
    icon: 'documenttext',
    iconName: 'check-circle',
    heroImage: 'essential-plan',
    including: 'common:paywall.plan.essential_annual.including',
    billed: 'common:paywall.plan.billed_yearly',
    month: 'common:paywall.plan.month',
    temperature: TemperatureValues.hot,
    featuresTitle: 'common:paywall.plan.features.essential_annual.title',
    keyFeatures: ESSENTIAL_FEATURES,
    features: FULL_ESSENTIAL_FEATURES,
    combinedFeatures: ESSENTIAL_WITH_FREE_FEATURES,
    price: 0,
    discountedPrice: 0,
    cta: '',
    order: 1,
    level: 3,
    term: {
      code: PlanTermEnum.Annual,
    },
  },
  {
    code: PlanCode.Essential,
    name: 'Essential',
    billingCode: BillingCodes.essentialMonth,
    translationCode: BillingCodes.essentialMonth,
    title: 'common:paywall.plan.essential_m2m.title',
    description: 'common:paywall.plan.essential_m2m.description',
    icon: 'documenttext',
    iconName: 'check-circle',
    heroImage: 'essential-plan',
    including: 'common:paywall.plan.essential_m2m.including',
    billed: 'common:paywall.plan.billed_monthly',
    month: 'common:paywall.plan.month',
    temperature: TemperatureValues.hot,
    featuresTitle: 'common:paywall.plan.features.essential_m2m.title',
    keyFeatures: ESSENTIAL_FEATURES,
    features: FULL_ESSENTIAL_FEATURES,
    combinedFeatures: ESSENTIAL_WITH_FREE_FEATURES,
    price: 0,
    discountedPrice: 0,
    cta: '',
    order: 1,
    level: 2,
    term: {
      code: PlanTermEnum.Monthly,
    },
  },
  {
    code: PlanCode.Free,
    name: 'Free',
    billingCode: BillingCodes.registration,
    translationCode: BillingCodes.registration,
    title: 'common:paywall.plan.registration.title',
    description: 'common:paywall.plan.registration.description',
    heroImage: 'name-of-hero',
    iconName: 'check-circle',
    billed: 'common:paywall.plan.billed_monthly',
    month: 'common:paywall.plan.month',
    temperature: TemperatureValues.vanilla,
    keyFeatures: FREE_FEATURES,
    features: FULL_FREE_FEATURES,
    price: 0,
    order: 0,
    level: 1,
    term: {
      code: PlanTermEnum.Monthly,
    },
  },
];

export type DeterminePlanVisibilityArgs = {
  plan: Partial<PlanType>;
  isTrialEligible: boolean;
  hasUltimatePlan: boolean;
  paywallLocation?: PaywallLocation;
  canUpgrade: boolean;
  isAnnualPlanForCurrentM2m: boolean;
  showPremiumOnly: boolean;
  showEssentialPlan: boolean;
  redirectToOpenCall: boolean;
};

const getStrikeThroughPrice = (plan: PlanType, plans: PlanType[]): string | number => {
  if (plan.discountedPrice) return plan.term.code === PlanTermEnum.Monthly ? plan.price : Number(plan.price) / 12;

  if (plan.term.code === PlanTermEnum.Monthly) {
    return '';
  }
  const monthlyPlan = plans.find((p) => p.billingCode === plan.billingCode.replace('_annual', '_m2m'));
  return monthlyPlan?.price ?? '';
};

type GetPlanCTAType = {
  t: TranslateFn;
  plan: PlanType;
  isUpgrade: boolean;
  currentPlanCode?: BillingCodes;
};

const getPlanCta = ({ t, plan, isUpgrade, currentPlanCode }: GetPlanCTAType): string => {
  if (plan.isUpgradeDisabled) {
    return t('common:paywall.plan.trial.noUpgradeCta');
  }

  if (plan.isTrialPeriodEnabled) {
    return t(`common:paywall.plan.trial.cta`, { days: plan.trialLengthDays });
  }

  if (plan.isReactivation && !plan.isCurrent) {
    return t('common:paywall.plan.reactivateNow');
  }

  if (plan.canRenew) {
    return t(`common:paywall.plan.renewNow`);
  }

  if (!isUpgrade) {
    return t(`common:paywall.plan.${plan.billingCode}.choose`);
  }

  if (plan.isCurrent) {
    return t(`common:paywall.plan.currentPlan`);
  }

  if (
    plan?.term?.code === PlanTermEnum.Annual &&
    currentPlanCode &&
    currentPlanCode === plan?.billingCode?.replace('_annual', '_m2m') &&
    plan.canUpgrade
  ) {
    return t(`common:paywall.plan.upgradeAnnual`);
  }

  if (plan.canUpgrade) {
    return t(`common:paywall.plan.upgrade`);
  }

  return ``;
};

type GetBilledAtTextType = {
  plan: PlanType;
  isTrialEligible?: boolean;
  t: TranslateFn;
  price: string;
};

const getBilledAtText = ({ plan, isTrialEligible, t, price }: GetBilledAtTextType): string => {
  const isAnnual = plan?.term?.code === PlanTermEnum.Annual;

  if (plan.isTrialPeriodEnabled && isAnnual) {
    return t(`common:paywall.plan.trial.annual.after_trial_price`, {
      price,
    });
  }

  if (plan.isTrialPeriodEnabled && !isAnnual) {
    return t(`common:paywall.plan.trial.m2m.after_trial_price`, {
      price,
    });
  }

  if (isAnnual && isTrialEligible) {
    return t(`common:paywall.plan.billed_yearly_at`, { price });
  }

  if (isAnnual && !isTrialEligible) {
    return t(`common:paywall.plan.billed_yearly`, { price });
  }

  if (plan.totalBillingCycles) {
    return t('common:paywall.plan.billed_for_months', { months: plan.totalBillingCycles });
  }

  return t(`common:paywall.plan.billed_monthly`, { price });
};

type TranslatePlansType = {
  t: TranslateFn;
  plans: PlanType[];
  isUpgrade: boolean;
  currentPlanCode?: BillingCodes;
  countryCode?: gqlCountry['code'];
  isTrialEligible?: boolean;
};

const translatePlans = ({
  t,
  plans,
  isUpgrade,
  currentPlanCode,
  isTrialEligible,
  countryCode,
}: TranslatePlansType): PlanType[] => {
  return plans.map((plan) => {
    const keyFeatures = plan.isTrialPeriodEnabled ? plan.combinedFeatures ?? plan.keyFeatures : plan.keyFeatures;
    const price = priceFormatter({
      price: Number(plan.price),
      currencyCode: plan.currencyCode,
      countryCode,
      t,
      roundingFloor: true,
    });

    return {
      ...plan,
      title: t(plan.title),
      description: t(plan.description),
      billed: getBilledAtText({
        plan,
        isTrialEligible,
        t,
        price,
      }),
      including: t(plan.including),
      month: t(plan.month),
      keyFeatures: keyFeatures.map((feature) => ({
        ...feature,
        label: feature?.label,
      })),
      features: plan.features.map((feature) => ({
        ...feature,
        label: feature?.label,
      })),
      featuresTitle: t(plan.featuresTitle),
      // This portion will need to be worked out to present the proper price for the user:
      price: `${plan.discountedPrice || plan.price}`,
      cta: getPlanCta({ t, plan, isUpgrade, currentPlanCode }),
      strikeThroughPrice: getStrikeThroughPrice(plan, plans),
      titleWithBillingInterval:
        plan.billingCode === BillingCodes.registration
          ? t(plan.title)
          : `${t(plan.title)} - ${t(`common:planterm.titlecase.${plan.term.code.toLowerCase()}`)}`,
    };
  });
};
const getPlanByBillingCode = (billingCode: string): PlanType | undefined => {
  return PLANS.find((plan) => plan.billingCode === billingCode);
};

// If the temperature value is invalid, set it to "vanilla"
const applyDefaultTemperature = ({ plans }: { plans: PlanType[] }) => {
  return plans.map((plan) => {
    return {
      ...plan,
      temperature: TemperatureValues[plan.temperature] ? plan.temperature : TemperatureValues.vanilla,
    };
  });
};

const getPlanTemperature = ({
  plan,
  isRegistration,
  modalVariant,
  isModal,
}: {
  plan: Partial<PlanType>;
  isRegistration: boolean;
  modalVariant: UpgradeModalExperimentGroups;
  isModal?: boolean;
}): TemperatureValues => {
  const isSinglePageUpgradeModal = modalVariant === UpgradeModalExperimentGroups.VariantA && isModal;
  const isLifestyleUpgradeModal = modalVariant === UpgradeModalExperimentGroups.VariantB && isModal;

  if (plan?.code === PlanCode.Premium && (isRegistration || isSinglePageUpgradeModal)) return TemperatureValues.warm;
  if (plan?.code === PlanCode.Premium && isLifestyleUpgradeModal) return TemperatureValues.gold;
  return plan.temperature;
};

const THIRTY_DAYS = 30;

export const usePlans = ({ subscription, paywallLocation, artistId, isModal, modalVariant }: UsePlansProps) => {
  const { myAccount, previousSubscription } = useUserContext();
  const isSinglePageUpgradeModal = modalVariant === UpgradeModalExperimentGroups.VariantA && isModal;
  const { isRegistration } = useRegistrationLocation({ paywallLocation });
  const countryCode = myAccount?.country?.code ?? 'US';
  const { t } = useTranslation();
  const plansArtistId = useRef(artistId);
  const { redirectToOpenCall } = useOpenCallRedirect();

  const addTrialPropertyToHeap = useCallback((data) => {
    if (!data?.billingPlans) {
      return;
    }
    const trialLengthDays = data?.billingPlans?.find((plan: PlanType) => plan?.trialLengthDays > 0)?.trialLengthDays;
    if (paywallLocation === PaywallLocation.SIGNUP) {
      if (trialLengthDays) {
        addUserProperties({ subscription: `${trialLengthDays}day-premiumtrial` });
      }
    } else {
      addUserProperties({ '7_day_premium_trial_via_upgrade_path': trialLengthDays === 7 });
    }
  }, []);

  const billingPath = useMemo(
    () => paywallLocationToBillingPath(paywallLocation, redirectToOpenCall),
    [paywallLocation, redirectToOpenCall]
  );
  const {
    data: plansData,
    error: plansError,
    loading: plansLoading,
    refetch: refetchPlans,
  } = useQuery(BillingPlansDoc, {
    variables: {
      input: {
        billingPath,
      },
    },
    skip: !artistId || !paywallLocation,
    onCompleted(data) {
      addTrialPropertyToHeap(data);
    },
  });

  const handleRefetchPlans = useCallback(() => refetchPlans({ input: { billingPath } }), [billingPath, refetchPlans]);

  const currentPlanCode = subscription?.plan?.billingCode;
  const isTrialEligible = useMemo(
    () => !plansLoading && plansData?.billingPlans?.some((plan) => plan?.trialLengthDays > 0),
    [plansData, plansLoading]
  );
  const isEligibleForCardlessTrial = useMemo(
    () => !plansLoading && plansData?.billingPlans?.some((plan) => plan?.trialType === TrialType.Cardless),
    [plansData, plansLoading]
  );

  const isSubscriptionWithin30Days = useMemo(() => {
    return (
      subscription?.expirationDate && differenceInDays(new Date(subscription.expirationDate), new Date()) < THIRTY_DAYS
    );
  }, [subscription]);

  const isWithinTrialPeriod = useMemo(() => {
    const { hasExpired, isInvalidDate, days, hours, minutes } = getTrialTimeLeft(subscription?.trialEndsAt as string);
    const isDueNow = days === 0 && hours === 0 && minutes === 0;
    return !isInvalidDate && !hasExpired && !isDueNow && subscription?.plan.provider !== PlanProvider.APPLE;
  }, [subscription]);

  const currentPlan = getPlanByBillingCode(currentPlanCode as BillingCodes);

  const isAnnualRenewalDue =
    isSubscriptionWithin30Days &&
    !isWithinTrialPeriod &&
    currentPlan?.term?.code === PlanTermEnum.Annual &&
    paywallLocation === PaywallLocation.MEMBERSHIP;

  const checkIsPremiumPlan = (billingCode: BillingCodes) =>
    [
      BillingCodes.premiumAnnual,
      BillingCodes.premiumMonth,
      BillingCodes.premiumComped,
      BillingCodes.premiumMonthSave10,
      BillingCodes.premiumMonthSave15,
      BillingCodes.premiumMonthDiscount15,
      BillingCodes.premiumMonthlyAt1499,
      BillingCodes.premiumAnnualAt1799,
      BillingCodes.premiumAnnualAt1499,
    ].includes(billingCode);

  const hasPriorPremiumPlan =
    checkIsPremiumPlan(previousSubscription?.plan?.billingCode as BillingCodes) &&
    moment().isAfter(moment(previousSubscription?.expirationDate as MomentInput)) &&
    paywallLocation &&
    !NON_REACTIVATE_LOCATIONS.includes(paywallLocation);

  const isReactivate = hasPriorPremiumPlan && !checkIsPremiumPlan(currentPlanCode as BillingCodes);

  const buildPlans = useCallback(
    (plansToBuild?: BillingPlansQuery['billingPlans']): PlanType[] => {
      if (plansToBuild?.length) {
        const availablePlans: PlanType[] = plansToBuild
          ?.map((planToBuild) => {
            const plan = PLANS.find((p) => planToBuild?.billingCode?.startsWith(p.billingCode));
            if (!plan) {
              return null;
            }
            const isPremiumPlan = checkIsPremiumPlan(planToBuild.billingCode as BillingCodes);
            const isCurrentPlan = subscription?.plan?.billingCode === planToBuild.billingCode && !isWithinTrialPeriod;
            const canRenew = isAnnualRenewalDue && isCurrentPlan;
            const isRegistrationPlan = planToBuild.billingCode === BillingCodes.registration;
            const isTrialPeriodEnabled = planToBuild.trialLengthDays > 0 && isTrialEligible;
            const canUpgrade = isWithinTrialPeriod || planToBuild?.level > (subscription?.plan?.level ?? -1);
            const isUpgradePath = paywallLocation !== PaywallLocation.SIGNUP;
            const isReactivation = isPremiumPlan && isReactivate;
            const isEssentialTrialPlan = planToBuild.trialLengthDays > 0 && plan.code === PlanCode.Essential;
            const isUpgradeDisabled = isWithinTrialPeriod && isEssentialTrialPlan;
            const isCardlessTrial = planToBuild.trialType === TrialType.Cardless;
            const title =
              planToBuild.discountType === DiscountTypeEnum.ReppedRegistrationDiscount
                ? `${t(plan.title)} ${t('common:membership')}`
                : plan.title;

            let planIncludingText = plan.including;
            if (isTrialPeriodEnabled && plan.code === PlanCode.Essential) {
              planIncludingText = '';
            }
            const { keyFeatures, features, combinedFeatures } = getPlanFeatures({
              code: plan.code,
              isRegistration,
              discountType: planToBuild.discountType,
            });

            const isPlanDiscounted = planToBuild.discountPercent > 0 && planToBuild.price < planToBuild.originalPrice;

            return {
              ...plan,
              title,
              billingCode: planToBuild.billingCode,
              discountOfferText: isPlanDiscounted ? 'common:paywall.plan.limitedTimeOffer' : '',
              discountPercent: planToBuild.discountPercent,
              discountType: planToBuild.discountType,
              name: planToBuild.name,
              price: planToBuild.originalPrice,
              discountedPrice: planToBuild.price !== planToBuild.originalPrice ? planToBuild.price : null,
              totalBillingCycles: planToBuild.billingCode === BillingCodes.premiumMonthDiscount15 ? 3 : null,
              provider: planToBuild.provider as PlanProvider,
              isCurrent: isCurrentPlan,
              canUpgrade,
              canRenew,
              currencyCode: planToBuild.currency.code,
              term: planToBuild.term,
              level: planToBuild.level,
              trialLengthDays: planToBuild.trialLengthDays ?? 0,
              isSecondary: isRegistrationPlan && !isCurrentPlan,
              including: planIncludingText,
              isTrialPeriodEnabled,
              isUpgradePath,
              isReactivation,
              isEssentialTrialPlan,
              isUpgradeDisabled,
              features,
              keyFeatures,
              combinedFeatures,
              shouldHidePlan:
                plan.code !== PlanCode.Premium &&
                !plansData?.billingPlans?.some((p) => p.billingCode === planToBuild.billingCode),
              displayAddPaymentSection: planToBuild.billingCode !== BillingCodes.registration,
              priceWithDecimal:
                planToBuild.trialLengthDays > 0 || planToBuild.billingCode === BillingCodes.registration,
              acceptTermsCheckboxKey:
                planToBuild.trialLengthDays > 0
                  ? 'common:paywall.plan.trial.legal.trialTerms'
                  : 'common:paywall.plan.legal.checkbox',
              temperature: getPlanTemperature({ plan, isRegistration, modalVariant, isModal }),
              backgroundImage: isSinglePageUpgradeModal ? `${appConfig.cdnRoot}/images/static/join-bg.jpeg` : null,
              isCardlessTrial,
            };
          })
          .filter(Boolean); // Filter out the null values

        // Translate the plans:
        const translatedPlans = translatePlans({
          t,
          currentPlanCode: subscription?.plan?.billingCode as BillingCodes,
          plans: availablePlans,
          isUpgrade: !!subscription?.plan?.billingCode,
          isTrialEligible,
          countryCode,
        });
        // Make sure the temperature value is valid
        const plansWithTemperature = applyDefaultTemperature({ plans: translatedPlans });

        // Sort the plans by order
        return plansWithTemperature.sort((a, b) => a.order - b.order);
      }

      return [];
    },
    [
      t,
      subscription,
      isTrialEligible,
      countryCode,
      isWithinTrialPeriod,
      isAnnualRenewalDue,
      paywallLocation,
      isReactivate,
      isRegistration,
      plansData?.billingPlans,
      isSinglePageUpgradeModal,
      modalVariant,
      isModal,
    ]
  );

  const plans = useMemo(() => buildPlans(plansData?.billingPlans), [buildPlans, plansData?.billingPlans]);
  const currentTranslatedPlan = useMemo(
    () =>
      paywallLocation !== PaywallLocation.SIGNUP && !isWithinTrialPeriod
        ? buildPlans([subscription?.plan] as BillingPlansQuery['billingPlans'])[0]
        : undefined,
    [buildPlans, paywallLocation, subscription?.plan, isWithinTrialPeriod]
  );
  const discountType = plans?.find((p) => p.discountType)?.discountType;

  // usePlans exists at the highest level which is the BillingPopupContextProvider.tsx this will never remount on page change including
  // context change. This is why we are using the artistId to indicate when a context change has occurred and will refetch the plansData
  useEffect(() => {
    if (plansArtistId.current !== artistId && paywallLocation && billingPath) {
      handleRefetchPlans().catch((e) => captureExceptionEvent(e));
      plansArtistId.current = artistId;
    }
  }, [artistId, billingPath, handleRefetchPlans, paywallLocation, refetchPlans]);

  return useMemo(
    () => ({
      plans,
      error: plansError,
      loading: plansLoading,
      isAnnualRenewalDue,
      isWithinTrialPeriod,
      currentPlan: currentTranslatedPlan,
      isTrialEligible,
      isReactivate,
      handleRefetchPlans,
      isEligibleForCardlessTrial,
      discountType,
    }),
    [
      plans,
      plansError,
      plansLoading,
      isAnnualRenewalDue,
      isWithinTrialPeriod,
      currentTranslatedPlan,
      isTrialEligible,
      isReactivate,
      handleRefetchPlans,
      isEligibleForCardlessTrial,
      discountType,
    ]
  );
};
